<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { subDays, subWeeks, subMonths, subYears, format, isAfter, isPast } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import axios from 'axios';

// Componentes
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ReportHeader from 'dashboard/routes/dashboard/settings/reports/components/ReportHeader.vue';
import BarChart from 'shared/components/charts/BarChart.vue';
import ClickableDonutChart from 'shared/components/charts/ClickableDonutChart.vue';
import LineChart from 'shared/components/charts/LineChart.vue';
import ConversationPopUp from 'dashboard/components/ConversationPopUp.vue';

// Componentes Personalizados
import RelatorioFiltros from '../components/RelatorioFiltros.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { accountId } = useAccount();

// Estados
const isLoading = ref(true);
const dateRange = ref([]);
const selectedInbox = ref({ id: 'all', name: 'Todos os Canais' });
const selectedPeriod = ref({ id: 'day', name: 'Diário' });
const isCustomDateRange = ref(false);
const activeGraphTab = ref('satisfaction');

// Dados reais da API
const apiData = ref(null);

// Estado para controlar tooltips
const showTooltip = ref({});

// Estado do modal de conversas
const showConversationModal = ref(false);
const modalConversations = ref([]);
const modalTitle = ref('');
const isLoadingModalData = ref(false);

// Funções para mostrar/esconder tooltips
const toggleTooltip = (key) => {
  showTooltip.value[key] = !showTooltip.value[key];
  // Fechar outros tooltips
  Object.keys(showTooltip.value).forEach(k => {
    if (k !== key) showTooltip.value[k] = false;
  });
};

// Handlers para cliques nos gráficos
const handleRatingBarClick = async (payload) => {
  const { index, label, value } = payload;
  
  modalTitle.value = `Conversas com avaliação ${label}`;
  modalConversations.value = [];
  showConversationModal.value = true;
  isLoadingModalData.value = true;
  
  try {
    const headers = getAuthHeaders();
    const params = {
      account_id: accountId.value,
      metric_type: 'satisfaction_range',
      metric_value: label,
      start_date: dateRange.value[0] ? format(dateRange.value[0], 'yyyy-MM-dd') : undefined,
      end_date: dateRange.value[1] ? format(dateRange.value[1], 'yyyy-MM-dd') : undefined
    };
    
    if (selectedInbox.value && selectedInbox.value.id !== 'all') {
      params.inbox_id = selectedInbox.value.id;
    }
    
    const response = await axios.get('/api/v1/ouroboros_dashboard/conversations_by_metric', {
      params,
      headers
    });
    
    modalConversations.value = response.data.conversations || [];
    const totalCount = response.data.total_count || 0;
    modalTitle.value = `Conversas com avaliação ${label} (${totalCount})`;
  } catch (error) {
    console.error('Error fetching conversations:', error);
  } finally {
    isLoadingModalData.value = false;
  }
};

const handleDonutClick = async (payload, chartType) => {
  const { index, label, value } = payload;
  
  modalTitle.value = `Conversas - ${label}`;
  modalConversations.value = [];
  showConversationModal.value = true;
  isLoadingModalData.value = true;
  
  try {
    const headers = getAuthHeaders();
    const params = {
      account_id: accountId.value,
      start_date: dateRange.value[0] ? format(dateRange.value[0], 'yyyy-MM-dd') : undefined,
      end_date: dateRange.value[1] ? format(dateRange.value[1], 'yyyy-MM-dd') : undefined
    };
    
    if (selectedInbox.value && selectedInbox.value.id !== 'all') {
      params.inbox_id = selectedInbox.value.id;
    }
    
    // Determinar o tipo de métrica baseado no tipo do gráfico
    const labelLower = label.toLowerCase();
    
    switch (chartType) {
      case 'customer_reaction':
        params.metric_type = 'customer_reaction';
        if (labelLower.includes('positiv')) {
          params.metric_value = 'positive';
        } else if (labelLower.includes('neutr')) {
          params.metric_value = 'neutral';
        } else if (labelLower.includes('negativ')) {
          params.metric_value = 'negative';
        }
        break;
        
      case 'agent_sentiment':
        params.metric_type = 'agent_sentiment';
        if (labelLower.includes('positiv')) {
          params.metric_value = 'positive';
        } else if (labelLower.includes('neutr')) {
          params.metric_value = 'neutral';
        } else if (labelLower.includes('negativ')) {
          params.metric_value = 'negative';
        }
        break;
        
      case 'agent_empathy':
        params.metric_type = 'agent_empathy';
        if (labelLower.includes('alt') || labelLower.includes('high')) {
          params.metric_value = 'high';
        } else if (labelLower.includes('médi') || labelLower.includes('medium')) {
          params.metric_value = 'medium';
        } else if (labelLower.includes('baix') || labelLower.includes('low')) {
          params.metric_value = 'low';
        }
        break;
        
      case 'problem_resolution':
        if (labelLower.includes('resolv') && !labelLower.includes('não')) {
          params.metric_type = 'problem_resolved';
        } else {
          params.metric_type = 'problem_not_resolved';
        }
        break;
    }
    
    const response = await axios.get('/api/v1/ouroboros_dashboard/conversations_by_metric', {
      params,
      headers
    });
    
    modalConversations.value = response.data.conversations || [];
    const totalCount = response.data.total_count || 0;
    modalTitle.value = `Conversas - ${label} (${totalCount})`;
  } catch (error) {
    console.error('Error fetching conversations:', error);
  } finally {
    isLoadingModalData.value = false;
  }
};

// Função para fechar o modal
const closeConversationModal = () => {
  showConversationModal.value = false;
  modalConversations.value = [];
  modalTitle.value = '';
  isLoadingModalData.value = false;
};

// Auth
const getCookie = (name) => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
};

const getAuthHeaders = () => {
  const cookie = getCookie('cw_d_session_info');
  if (!cookie) return {};
  try {
    const sessionInfo = JSON.parse(cookie);
    return {
      'access-token': sessionInfo['access-token'],
      client: sessionInfo['client'],
      uid: sessionInfo['uid']
    };
  } catch (e) {
    console.error('Erro ao ler o cookie:', e);
    return {};
  }
};

// Opções de agrupamento
const periodOptions = [
  { id: 'day', name: 'Diário' },
  { id: 'week', name: 'Semanal' },
  { id: 'month', name: 'Mensal' },
  { id: 'year', name: 'Anual' },
  { id: 'custom', name: 'Personalizado' }
];

// Carregar lista de inboxes
const inboxes = useMapGetter('inboxes/getInboxes');
const getInboxDisplayName = useMapGetter('inboxMetadata/getInboxDisplayName');
const inboxOptions = computed(() => {
  return [
    { id: 'all', name: 'Todos os Canais' },
    ...(inboxes.value || []).map(inbox => ({
      id: inbox.id,
      name: getInboxDisplayName.value(inbox.id) || inbox.name || 'Desconhecido'
    }))
  ];
});

// Ajustar dateRange com base no período selecionado
const updateDateRangeBasedOnPeriod = () => {
  if (isCustomDateRange.value) return;
  
  const endDate = new Date();
  let startDate;
  
  switch (selectedPeriod.value.id) {
    case 'day':
      startDate = subDays(endDate, 1);
      break;
    case 'week':
      startDate = subDays(endDate, 7);
      break;
    case 'month':
      startDate = subMonths(endDate, 1);
      break;
    case 'year':
      startDate = subYears(endDate, 1);
      break;
    default:
      startDate = subDays(endDate, 7);
  }
  
  dateRange.value = [startDate, endDate];
};

const fetchData = async () => {
  // Esta função agora é apenas um placeholder
};

// Dados do relatório - inicializa com estrutura vazia
const reportData = ref({
  totalConversations: 0,
  totalMessagesSent: 0,
  customerMessageCount: 0,
  currentConversionRate: 0,
  totalConversionRate: 0,
  outboundConversionRate: 0,
  customerSatisfaction: 0,
  problemResolutionRate: 0,
  newCustomerRate: 0,
  outboundRate: 0,
  outboundCount: 0,
  customersLostCount: 0,
  disqualificationRate: 0,
  disqualifiedCount: 0,
  usedHumanEscalationCount: 0,
  sdrQualificationRate: 0,
  appointmentCompletionRate: 0,
  salesCompletionRate: 0,
  sdrQualificationCount: 0,
  appointmentCompletionCount: 0,
  salesCompletionCount: 0,
  customerReaction: {
    positive: 0,
    neutral: 0,
    negative: 0
  },
  agentSentiment: {
    positive: 0,
    neutral: 0,
    negative: 0
  },
  agentEmpathy: {
    high: 0,
    medium: 0,
    low: 0
  },
  inappropriateLanguage: {
    customer: 0,
    agent: 0
  },
  conversationRatings: {
    '0-10': 0,
    '10-20': 0,
    '20-30': 0,
    '30-40': 0,
    '40-50': 0,
    '50-60': 0,
    '60-70': 0,
    '70-80': 0,
    '80-90': 0,
    '90-100': 0
  },
  timeData: {
    labels: [],
    satisfaction: [],
    conversions: [],
    conversionsPercentage: [],
    humanEscalation: []
  }
});

// Handler para alterações de filtro
const handleFilterChange = (filters) => {
  if (filters.isCustomDate !== undefined) {
    isCustomDateRange.value = filters.isCustomDate;
  }
  
  if (filters.dateRange && filters.dateRange.length === 2) {
    dateRange.value = filters.dateRange;
    if (filters.isCustomDate) {
      selectedPeriod.value = { id: 'custom', name: 'Personalizado' };
    }
  }
  
  if (filters.period && filters.period.id) {
    selectedPeriod.value = filters.period;
  }
  
  if (filters.inbox && filters.inbox.id) {
    selectedInbox.value = filters.inbox;
  }
};

// Handler para quando os dados são carregados da API
const handleDataLoaded = (data) => {
  isLoading.value = false;
  apiData.value = data;
  
  if (data) {
    const satisfactionRanges = data.satisfaction_ranges || {};
    
    reportData.value = {
      totalConversations: data.total_conversations || 0,
      totalMessagesSent: data.total_messages_sent || 0,
      customerMessageCount: data.customer_message_count || 0,
      currentConversionRate: data.current_conversion_rate || 0,
      totalConversionRate: data.total_conversion_rate || 0,
      outboundConversionRate: data.outbound_conversion_rate || 0,
      customerSatisfaction: data.customer_satisfaction || 0,
      problemResolutionRate: data.problem_resolution_rate || 0,
      newCustomerRate: data.new_customer_rate || 0,
      outboundRate: data.outbound_rate || 0,
      outboundCount: data.outbound_count || 0,
      customersLostCount: data.customers_lost_count || 0,
      disqualificationRate: data.disqualification_rate || 0,
      disqualifiedCount: data.disqualified_count || 0,
      usedHumanEscalationCount: data.used_human_escalation_count || 0,
      sdrQualificationRate: data.sdr_qualification_rate || 0,
      appointmentCompletionRate: data.appointment_completion_rate || 0,
      salesCompletionRate: data.sales_completion_rate || 0,
      sdrQualificationCount: data.sdr_qualification_count || 0,
      appointmentCompletionCount: data.appointment_completion_count || 0,
      salesCompletionCount: data.sales_completion_count || 0,
      customerReaction: data.customer_reaction || { positive: 0, neutral: 0, negative: 0 },
      agentSentiment: data.agent_sentiment || { positive: 0, neutral: 0, negative: 0 },
      agentEmpathy: data.agent_empathy || { high: 0, medium: 0, low: 0 },
      inappropriateLanguage: data.inappropriate_language || { customer: 0, agent: 0 },
      conversationRatings: satisfactionRanges,
      timeData: {
        labels: data.time_data?.labels || [],
        satisfaction: data.time_data?.satisfaction || [],
        conversions: data.time_data?.conversions || [],
        conversionsPercentage: data.time_data?.conversions_percentage || [],
        humanEscalation: data.time_data?.human_escalation || []
      }
    };
  }
};

// Inicializar dados ao montar o componente
onMounted(() => {
  store.dispatch('inboxes/get');
});

const navegarParaTabelas = () => {
  router.push({ name: 'ouroboros_tables' });
};
const navegarParaConversas = () => {
  router.push({ name: 'home' });
};
const navegarParaAgentis = () => {
  router.push({ name: 'agentis_list' });
};

// Dados para os gráficos
const ratingChartData = computed(() => {
  const chartData = {
    labels: ['0-10%', '10-20%', '20-30%', '30-40%', '40-50%', '50-60%', '60-70%', '70-80%', '80-90%', '90-100%'],
    datasets: [
      {
        label: 'Quantidade de Avaliações',
        data: [
          reportData.value.conversationRatings['0-10'] || 0,
          reportData.value.conversationRatings['10-20'] || 0,
          reportData.value.conversationRatings['20-30'] || 0,
          reportData.value.conversationRatings['30-40'] || 0,
          reportData.value.conversationRatings['40-50'] || 0,
          reportData.value.conversationRatings['50-60'] || 0,
          reportData.value.conversationRatings['60-70'] || 0,
          reportData.value.conversationRatings['70-80'] || 0,
          reportData.value.conversationRatings['80-90'] || 0,
          reportData.value.conversationRatings['90-100'] || 0
        ],
        backgroundColor: [
          'rgba(239, 68, 68, 0.6)',   // 0-10% - Vermelho forte
          'rgba(248, 113, 113, 0.6)', // 10-20% - Vermelho médio
          'rgba(251, 146, 60, 0.6)',  // 20-30% - Laranja
          'rgba(253, 186, 116, 0.6)', // 30-40% - Laranja claro
          'rgba(254, 240, 138, 0.6)', // 40-50% - Amarelo
          'rgba(217, 249, 157, 0.6)', // 50-60% - Verde amarelado
          'rgba(134, 239, 172, 0.6)', // 60-70% - Verde claro
          'rgba(52, 211, 153, 0.6)',  // 70-80% - Verde médio
          'rgba(16, 185, 129, 0.6)',  // 80-90% - Verde escuro
          'rgba(5, 150, 105, 0.6)'    // 90-100% - Verde muito escuro
        ]
      }
    ]
  };
  return chartData;
});

const timeChartData = computed(() => {
  return {
    labels: reportData.value.timeData.labels,
    datasets: [
      {
        label: 'Satisfação Média',
        data: reportData.value.timeData.satisfaction,
        borderColor: 'rgba(59, 130, 246, 1)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
        fill: true,
        pointBackgroundColor: 'rgba(59, 130, 246, 1)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 7
      }
    ]
  };
});

const conversionChartData = computed(() => {
  return {
    labels: reportData.value.timeData.labels,
    datasets: [
      {
        label: 'Conversões',
        data: reportData.value.timeData.conversions,
        borderColor: 'rgba(16, 185, 129, 1)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        tension: 0.4,
        fill: true,
        pointBackgroundColor: 'rgba(16, 185, 129, 1)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 7,
        percentageData: reportData.value.timeData.conversionsPercentage
      }
    ]
  };
});

// Dados para os gráficos de rosquinha
const reactionDonutData = computed(() => {
  return {
    labels: ['Positiva', 'Neutra', 'Negativa'],
    datasets: [
      {
        data: [
          reportData.value.customerReaction.positive,
          reportData.value.customerReaction.neutral,
          reportData.value.customerReaction.negative
        ],
        backgroundColor: [
          'rgba(34, 197, 94, 0.8)',  // Verde para positiva
          'rgba(234, 179, 8, 0.8)',  // Amarelo para neutra
          'rgba(239, 68, 68, 0.8)'   // Vermelho para negativa
        ],
        borderColor: [
          'rgba(34, 197, 94, 1)',
          'rgba(234, 179, 8, 1)',
          'rgba(239, 68, 68, 1)'
        ],
        borderWidth: 1
      }
    ]
  };
});

const problemResolutionDonutData = computed(() => {
  return {
    labels: ['Resolvidos', 'Não Resolvidos'],
    datasets: [
      {
        data: [
          reportData.value.problemResolutionRate,
          100 - reportData.value.problemResolutionRate
        ],
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',  // Azul para resolvidos
          'rgba(156, 163, 175, 0.8)'  // Cinza para não resolvidos
        ],
        borderColor: [
          'rgba(59, 130, 246, 1)',
          'rgba(156, 163, 175, 1)'
        ],
        borderWidth: 1
      }
    ]
  };
});

const agentEmpathyDonutData = computed(() => {
  return {
    labels: ['Alta', 'Média', 'Baixa'],
    datasets: [
      {
        data: [
          reportData.value.agentEmpathy.high,
          reportData.value.agentEmpathy.medium,
          reportData.value.agentEmpathy.low
        ],
        backgroundColor: [
          'rgba(147, 51, 234, 0.8)',  // Roxo para alta
          'rgba(59, 130, 246, 0.8)',  // Azul para média
          'rgba(249, 115, 22, 0.8)'   // Laranja para baixa
        ],
        borderColor: [
          'rgba(147, 51, 234, 1)',
          'rgba(59, 130, 246, 1)',
          'rgba(249, 115, 22, 1)'
        ],
        borderWidth: 1
      }
    ]
  };
});


const agentSentimentDonutData = computed(() => {
  return {
    labels: ['Positivo', 'Neutro', 'Negativo'],
    datasets: [
      {
        data: [
          reportData.value.agentSentiment.positive,
          reportData.value.agentSentiment.neutral,
          reportData.value.agentSentiment.negative
        ],
        backgroundColor: [
          'rgba(34, 197, 94, 0.8)',   // Verde para positivo
          'rgba(234, 179, 8, 0.8)',   // Amarelo para neutro
          'rgba(239, 68, 68, 0.8)'    // Vermelho para negativo
        ],
        borderColor: [
          'rgba(34, 197, 94, 1)',
          'rgba(234, 179, 8, 1)',
          'rgba(239, 68, 68, 1)'
        ],
        borderWidth: 1
      }
    ]
  };
});

// Função para verificar se os dados estão vazios
const isChartDataEmpty = (chartData) => {
  if (!chartData || !chartData.datasets || !chartData.datasets.length) return true;
  const dataset = chartData.datasets[0];
  if (!dataset.data || !dataset.data.length) return true;
  return dataset.data.every(value => value === 0 || value === null || value === undefined);
};

// Computed para verificar se os dados dos gráficos estão vazios
const isRatingChartEmpty = computed(() => isChartDataEmpty(ratingChartData.value));
const isTimeChartEmpty = computed(() => isChartDataEmpty(timeChartData.value));
const isConversionChartEmpty = computed(() => isChartDataEmpty(conversionChartData.value));
const isReactionDonutEmpty = computed(() => isChartDataEmpty(reactionDonutData.value));
const isProblemResolutionDonutEmpty = computed(() => {
  // Considera vazio se a taxa de resolução for 0 e não houver dados de conversas
  return reportData.value.problemResolutionRate === 0 && reportData.value.totalConversations === 0;
});
const isAgentEmpathyDonutEmpty = computed(() => isChartDataEmpty(agentEmpathyDonutData.value));
const isAgentSentimentDonutEmpty = computed(() => isChartDataEmpty(agentSentimentDonutData.value));

// Configurações para os gráficos
const barChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top',
      labels: {
        font: {
          size: 12
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true
    }
  }
};

const lineChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top',
      labels: {
        font: {
          size: 12
        }
      }
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      callbacks: {
        label: function(context) {
          let label = context.dataset.label || '';
          if (label) {
            label += ': ';
          }
          if (context.parsed.y !== null) {
            if (context.dataset.label === 'Satisfação Média') {
              label += context.parsed.y.toFixed(2) + ' / 5';
            } else if (context.dataset.label === 'Conversões') {
              label += context.parsed.y + ' conversões';
              if (context.dataset.percentageData && context.dataset.percentageData[context.dataIndex] !== undefined) {
                label += ' (' + context.dataset.percentageData[context.dataIndex].toFixed(1) + '%)';
              }
            } else {
              label += context.parsed.y.toFixed(2) + '%';
            }
          }
          return label;
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        callback: function(value) {
          return value;
        }
      }
    },
    x: {
      grid: {
        display: false
      }
    }
  },
  interaction: {
    mode: 'nearest',
    axis: 'x',
    intersect: false
  }
};

const satisfactionLineChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top',
      labels: {
        font: {
          size: 12
        }
      }
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      callbacks: {
        label: function(context) {
          let label = context.dataset.label || '';
          if (label) {
            label += ': ';
          }
          if (context.parsed.y !== null) {
            label += context.parsed.y.toFixed(2) + '%';
          }
          return label;
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      max: 100,
      ticks: {
        stepSize: 20,
        callback: function(value) {
          return value + '%';
        }
      }
    },
    x: {
      grid: {
        display: false
      }
    }
  },
  interaction: {
    mode: 'nearest',
    axis: 'x',
    intersect: false
  }
};

const donutChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: '60%',
  plugins: {
    legend: {
      position: 'right',
      labels: {
        font: {
          size: 12
        },
        padding: 20
      }
    },
    tooltip: {
      callbacks: {
        label: function(context) {
          const label = context.label || '';
          const value = context.parsed || 0;
          const total = context.dataset.data.reduce((sum, val) => sum + val, 0);
          const percentage = ((value / total) * 100).toFixed(1);
          
          // Calcular o número real de conversas
          const totalConversations = reportData.value.totalConversations || 0;
          const conversationCount = Math.round((percentage / 100) * totalConversations);
          
          return `${label}: ${percentage}% (${conversationCount})`;
        }
      }
    }
  }
};


// Função para exportar os dados
const exportData = () => {
  alert('Exportando dados do relatório...');
};

// Computed para gerar insights simples
const insights = computed(() => {
  const insightsList = [];
  
  // Só mostra insights se houver dados
  if (reportData.value.totalConversations === 0) {
    return insightsList;
  }
  
  // Satisfação do cliente
  if (reportData.value.customerSatisfaction < 50) {
    insightsList.push({
      type: 'critical',
      icon: 'i-lucide-alert-triangle',
      text: `Satisfação crítica em ${reportData.value.customerSatisfaction.toFixed(0)}%. Isso está muito abaixo da meta de 80% e precisa de atenção urgente.`
    });
  } else if (reportData.value.customerSatisfaction < 70) {
    insightsList.push({
      type: 'warning',
      icon: 'i-lucide-alert-circle',
      text: `Satisfação em ${reportData.value.customerSatisfaction.toFixed(0)}% está abaixo da meta de 80%. Revise os processos de atendimento.`
    });
  } else if (reportData.value.customerSatisfaction >= 80) {
    insightsList.push({
      type: 'positive',
      icon: 'i-lucide-check-circle',
      text: `Excelente satisfação de ${reportData.value.customerSatisfaction.toFixed(0)}%! Continue mantendo a qualidade do atendimento.`
    });
  }
  
  // Taxa de conversão
  if (reportData.value.totalConversionRate < 5) {
    insightsList.push({
      type: 'critical',
      icon: 'i-lucide-trending-down',
      text: `Taxa de conversão muito baixa: apenas ${reportData.value.totalConversionRate.toFixed(1)}%. A meta mínima é 15%.`
    });
  } else if (reportData.value.totalConversionRate < 15) {
    insightsList.push({
      type: 'warning',
      icon: 'i-lucide-activity',
      text: `Conversão em ${reportData.value.totalConversionRate.toFixed(1)}% pode melhorar. Foque em qualificação e abordagem inicial.`
    });
  } else if (reportData.value.totalConversionRate >= 20) {
    insightsList.push({
      type: 'positive',
      icon: 'i-lucide-rocket',
      text: `Taxa de conversão excelente: ${reportData.value.totalConversionRate.toFixed(1)}%. Performance acima da meta de 15%.`
    });
  }
  
  // Taxa de resolução
  if (reportData.value.problemResolutionRate < 60) {
    insightsList.push({
      type: 'warning',
      icon: 'i-lucide-help-circle',
      text: `Taxa de resolução baixa (${reportData.value.problemResolutionRate.toFixed(0)}%). Muitos problemas não estão sendo resolvidos na primeira interação.`
    });
  } else if (reportData.value.problemResolutionRate >= 85) {
    insightsList.push({
      type: 'positive',
      icon: 'i-lucide-check-circle-2',
      text: `Ótima taxa de resolução: ${reportData.value.problemResolutionRate.toFixed(0)}% dos problemas são resolvidos efetivamente.`
    });
  }
  
  
  // Empatia do agente
  if (reportData.value.agentEmpathy.low > 30) {
    insightsList.push({
      type: 'warning',
      icon: 'i-lucide-heart',
      text: `${reportData.value.agentEmpathy.low}% das conversas tiveram baixa empatia dos seus atendentes. Isso afeta diretamente a satisfação do cliente.`
    });
  }
  
  // Linguagem inadequada
  if (reportData.value.inappropriateLanguage.customer > 5) {
    insightsList.push({
      type: 'warning',
      icon: 'i-lucide-message-square-warning',
      text: `Linguagem inadequada detectada em ${reportData.value.inappropriateLanguage.customer.toFixed(1)}% das conversas, acima do limite de 5%.`
    });
  }
  
  // Disparos outbound
  if (reportData.value.outboundRate > 10 && reportData.value.outboundConversionRate < reportData.value.totalConversionRate) {
    insightsList.push({
      type: 'info',
      icon: 'i-lucide-send',
      text: `Disparos ativos convertendo apenas ${reportData.value.outboundConversionRate.toFixed(1)}%, abaixo da média geral. Revise a segmentação.`
    });
  }
  
  
  return insightsList;
});

// Computed para verificar se deve mostrar o bloco de insights
const hasInsights = computed(() => {
  return insights.value.length > 0 && reportData.value.totalConversations > 0;
});

// Computed para verificar se há dados no funil
const hasFunnelData = computed(() => {
  return reportData.value.sdrQualificationRate > 0 || 
         reportData.value.salesCompletionRate > 0 || 
         reportData.value.appointmentCompletionRate > 0 ||
         reportData.value.sdrQualificationCount > 0 ||
         reportData.value.salesCompletionCount > 0 ||
         reportData.value.appointmentCompletionCount > 0;
});
</script>

<template>
  <div class="reports--wrapper overflow-auto bg-n-background w-full px-8 xl:px-0">
    <div class="max-w-[1200px] mx-auto pb-12">
      <!-- Header e Filtros -->
      <ReportHeader header-title="Dashboard de Métricas de Performance">
        <div class="flex space-x-2">
          <!--
          <Button
            label="Exportar Dados"
            icon="i-lucide-download"
            size="sm"
            @click="exportData"
          />
          -->
        </div>
      </ReportHeader>
      
      <!-- Mensagem de descrição do dashboard -->
      <div class="mb-6 p-4 bg-n-blue-text/5 rounded-lg border border-n-blue-text/20">
        <p class="text-sm text-n-slate-11 flex items-start">
          <span class="i-lucide-info mr-2 mt-0.5 text-n-blue-text"></span>
          <span>
            <strong>Bem-vindo ao Dashboard de Métricas!</strong> Este dashboard apresenta indicadores-chave de performance (KPIs) das conversas com clientes, 
            incluindo métricas de engajamento, satisfação, conversão e análise comportamental.
          </span>
        </p>
      </div>
      
      <!-- Filtros -->
      <RelatorioFiltros
        :inbox-options="inboxOptions"
        :period-options="periodOptions"
        v-model:selected-inbox="selectedInbox"
        v-model:selected-period="selectedPeriod"
        v-model:date-range="dateRange"
        :auth-headers="getAuthHeaders()"
        :is-custom-date-range="isCustomDateRange"
        @filter-change="handleFilterChange"
        @data-loaded="handleDataLoaded"
      />
      
      <!-- Loading Estado -->
      <div v-if="isLoading" class="flex justify-center items-center py-16">
        <Spinner />
      </div>
      
      <div v-else>
        <!-- Aviso de sem dados com CTA -->
        <div v-if="reportData.totalConversations === 0" class="mt-6 mb-8">
          <div class="bg-gradient-to-r from-indigo-50 to-indigo-100 border border-n-brand rounded-lg p-6">
            <div class="flex flex-col md:flex-row items-center gap-6">
              <div class="flex-shrink-0">
                <span class="i-lucide-rocket text-5xl text-n-blue-text"></span>
              </div>
              <div class="flex-1 text-center md:text-left">
                <h3 class="text-lg font-semibold text-blue-900 mb-2">Comece a gerar insights valiosos!</h3>
                <p class="text-blue-800 mb-3">
                  Ainda não há dados para o período selecionado, mas este dashboard está pronto para mostrar métricas poderosas assim que você começar a usar a plataforma.
                </p>
                <p class="text-sm text-blue-700 mb-4">
                  Veja abaixo todas as métricas e análises que estarão disponíveis quando suas conversas começarem a fluir.
                </p>
                <div class="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                  <Button
                    label="Iniciar primeira conversa"
                    icon="i-lucide-message-circle"
                    color="blue"
                    size="sm"
                    @click="navegarParaConversas"
                  />
                  <Button
                    label="Configurar agentes"
                    icon="i-lucide-bot"
                    color="blue"
                    variant="smooth"
                    size="sm"
                    @click="navegarParaAgentis"
                  />
                </div>
              </div>
            </div>
            <div class="mt-4 pt-4 border-t border-blue-200">
              <p class="text-xs text-blue-700 text-center">
                <span class="i-lucide-info mr-1"></span>
                Período selecionado: {{ dateRange[0] ? format(dateRange[0], 'dd/MM/yyyy', { locale: ptBR }) : '' }} - {{ dateRange[1] ? format(dateRange[1], 'dd/MM/yyyy', { locale: ptBR }) : '' }} | Canal: {{ selectedInbox.name }}
              </p>
            </div>
          </div>
        </div>
        
        <!-- KPIs Principais em Destaque -->
        <div class="mb-8">
          <h3 class="text-lg font-semibold mb-4 flex items-center text-n-slate-12">
            <span class="i-lucide-star mr-2 text-amber-500"></span>
            Indicadores Principais
          </h3>
          
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <!-- Taxa de Conversão Total -->
            <div class="outline-1 outline outline-n-container relative shadow hover:shadow-md rounded-xl p-5 transition-all duration-300 bg-n-solid-2">
              <div class="relative">
                <div class="flex items-center justify-between mb-3">
                  <div class="flex items-center">
                    <div class="p-2 bg-woot-100 rounded-lg mr-3 flex">
                      <span class="i-lucide-trending-up text-n-blue-text text-xl"></span>
                    </div>
                    <h4 class="text-base font-bold m-0">Taxa de Conversão Total</h4>
                  </div>
                  <div class="group relative">
                    <span class="i-lucide-info text-n-slate-11 text-sm cursor-help hover:text-n-slate-12 transition-colors"></span>
                    <div class="invisible group-hover:visible absolute right-0 top-6 w-56 p-2 bg-n-solid-2 rounded-lg shadow-xl z-10 text-xs text-n-slate-11 border border-n-weak">
                      <strong>Taxa de Conversão:</strong> Percentual de conversas que resultaram em uma ação desejada (qualificação, agendamento ou venda).<br><br>
                      <strong>Conversa</strong> representa cada conjunto finalizado de interações entre cliente e equipe, podendo haver várias conversas com o mesmo contato.
                    </div>
                  </div>
                </div>
                <div class="mt-4 mb-2 text-3xl font-bold text-n-slate-12">
                  {{ reportData.totalConversionRate.toFixed(1) }}%
                </div>
                <div class="text-sm mt-3 font-medium flex items-center" 
                    :class="reportData.totalConversionRate >= 15 ? 'text-emerald-600' : 'text-amber-600'">
                  <span v-if="reportData.totalConversionRate >= 15" class="i-lucide-check-circle mr-1"></span>
                  <span v-else class="i-lucide-alert-circle mr-1"></span>
                  {{ reportData.totalConversionRate === 0 && reportData.totalConversations === 0 ? '' : reportData.totalConversionRate >= 15 ? 'Desempenho acima da meta' : 'Abaixo da meta (15%)' }}
                </div>
              </div>
            </div>
            
            <!-- Satisfação do Cliente -->
            <div class="outline-1 outline outline-n-container relative shadow hover:shadow-md rounded-xl p-5 transition-all duration-300 bg-n-solid-2">
              <div class="relative">
                <div class="flex items-center justify-between mb-3">
                  <div class="flex items-center">
                    <div class="p-2 bg-woot-100 rounded-lg mr-3 flex">
                      <span class="i-lucide-smile text-n-blue-text text-xl"></span>
                    </div>
                    <h4 class="text-base font-bold m-0">Satisfação do Cliente</h4>
                  </div>
                  <div class="group relative">
                    <span class="i-lucide-info text-n-slate-11 text-sm cursor-help hover:text-n-slate-12 transition-colors"></span>
                    <div class="invisible group-hover:visible absolute right-0 top-6 w-56 p-2 bg-n-solid-2 rounded-lg shadow-xl z-10 text-xs text-n-slate-11 border border-n-weak">
                      <strong>Satisfação do Cliente:</strong> Média das avaliações dadas pelos clientes após as conversas (escala de 0 a 100%).
                    </div>
                  </div>
                </div>
                <div class="mt-4 mb-2 text-3xl font-bold text-n-slate-12">
                  {{ reportData.customerSatisfaction.toFixed(1) }}%
                </div>
                <div class="text-sm mt-3 font-medium flex items-center" 
                    :class="reportData.customerSatisfaction >= 80 ? 'text-emerald-600' : reportData.customerSatisfaction >= 60 ? 'text-amber-600' : 'text-red-600'">
                  <span v-if="reportData.customerSatisfaction >= 80" class="i-lucide-heart mr-1"></span>
                  <span v-else-if="reportData.customerSatisfaction >= 60" class="i-lucide-meh mr-1"></span>
                  <span v-else class="i-lucide-frown mr-1"></span>
                  {{ reportData.customerSatisfaction === 0 && reportData.totalConversations === 0 ? '' : reportData.customerSatisfaction >= 80 ? 'Clientes muito satisfeitos' : reportData.customerSatisfaction >= 60 ? 'Satisfação moderada' : 'Necessita melhoria urgente' }}
                </div>
              </div>
            </div>
            
            <!-- Problemas Resolvidos -->
            <div class="outline-1 outline outline-n-container relative shadow hover:shadow-md rounded-xl p-5 transition-all duration-300 bg-n-solid-2">
              <div class="relative">
                <div class="flex items-center justify-between mb-3">
                  <div class="flex items-center">
                    <div class="p-2 bg-woot-100 rounded-lg mr-3 flex">
                      <span class="i-lucide-check-circle text-n-blue-text text-xl"></span>
                    </div>
                    <h4 class="text-base font-bold m-0">Taxa de Resolução</h4>
                  </div>
                  <div class="group relative">
                    <span class="i-lucide-info text-n-slate-11 text-sm cursor-help hover:text-n-slate-12 transition-colors"></span>
                    <div class="invisible group-hover:visible absolute right-0 top-6 w-56 p-2 bg-n-solid-2 rounded-lg shadow-xl z-10 text-xs text-n-slate-11 border border-n-weak">
                      <strong>Taxa de Resolução:</strong> Percentual de conversas onde o problema ou dúvida do cliente foi completamente resolvido.<br><br>
                      <strong>Conversa</strong> representa cada conjunto finalizado de interações entre cliente e equipe, podendo haver várias conversas com o mesmo contato.
                    </div>
                  </div>
                </div>
                <div class="mt-4 mb-2 text-3xl font-bold text-n-slate-12">
                  {{ reportData.problemResolutionRate.toFixed(1) }}%
                </div>
                <div class="text-sm mt-3 font-medium flex items-center" 
                    :class="reportData.problemResolutionRate >= 70 ? 'text-emerald-600' : 'text-amber-600'">
                  <span v-if="reportData.problemResolutionRate >= 70" class="i-lucide-shield-check mr-1"></span>
                  <span v-else class="i-lucide-shield-alert mr-1"></span>
                  {{ reportData.problemResolutionRate === 0 && reportData.totalConversations === 0 ? '' : reportData.problemResolutionRate >= 70 ? 'Alta eficiência na resolução' : 'Oportunidade de melhoria' }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Dicas e Insights -->
        <div v-if="hasInsights" class="mb-8">
          <!-- Conteúdo principal -->
          <div class="rounded-2xl outline-1 outline outline-n-container bg-n-solid-2 p-6 shadow-sm">
            <!-- Header com destaque -->
            <div class="flex items-center justify-between mb-5">
              <div class="flex items-center">
                <div class="p-3 from-blue-500 to-indigo-600 rounded-xl">
                  <span class="i-lucide-lightbulb text-2xl text-n-blue-text"></span>
                </div>
                <div class="ml-4">
                  <h4 class="text-xl font-bold text-gray-900 m-0">Insights e Recomendações</h4>
                  <p class="text-sm text-gray-600 mt-0.5">Análise inteligente dos seus dados</p>
                </div>
              </div>
              <div class="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                {{ insights.length }} {{ insights.length === 1 ? 'insight' : 'insights' }}
              </div>
            </div>
            
            <!-- Lista de insights com design aprimorado -->
            <div class="space-y-3">
              <div v-for="(insight, index) in insights" :key="index" 
                class="group relative transform transition-all duration-300 hover:scale-[1.02] hover:shadow-lg rounded-xl"
                :class="{
                  '': insight.type === 'critical',
                  '': insight.type === 'warning',
                  '': insight.type === 'positive',
                  '': insight.type === 'info'
                }">
                
                <!-- Card do insight -->
                <div 
                  class="relative overflow-hidden rounded-xl p-4 border-2 transition-all duration-300"
                  :class="{
                    'bg-gradient-to-r from-red-50 to-red-100/50 border-red-300': insight.type === 'critical',
                    'bg-gradient-to-r from-amber-50 to-amber-100/50 border-amber-300': insight.type === 'warning',
                    'bg-gradient-to-r from-emerald-50 to-emerald-100/50 border-emerald-300': insight.type === 'positive',
                    'bg-gradient-to-r from-blue-50 to-blue-100/50 border-blue-300': insight.type === 'info'
                  }">
                  
                  <!-- Conteúdo do insight -->
                  <div class="flex items-start">
                    <!-- Ícone com animação -->
                    <div class="relative">
                      <span :class="[
                        insight.icon,
                        'text-xl flex-shrink-0 transition-transform duration-300 group-hover:scale-110',
                        {
                          'text-red-600': insight.type === 'critical',
                          'text-amber-600': insight.type === 'warning',
                          'text-emerald-600': insight.type === 'positive',
                          'text-blue-600': insight.type === 'info'
                        }
                      ]"></span>
                      <!-- Pulse animation para insights críticos -->
                      <div v-if="insight.type === 'critical'" 
                        class="absolute inset-0 rounded-full animate-ping opacity-30"></div>
                    </div>
                    
                    <!-- Texto do insight -->
                    <div class="ml-3 flex-1">
                      <p class="text-sm font-medium leading-relaxed m-0"
                        :class="{
                          'text-red-800': insight.type === 'critical',
                          'text-amber-800': insight.type === 'warning',
                          'text-emerald-800': insight.type === 'positive',
                          'text-blue-800': insight.type === 'info'
                        }">
                        {{ insight.text }}
                      </p>
                      
                      <!-- Badge de tipo -->
                      <div class="mt-2 inline-flex items-center">
                        <span class="text-xs font-semibold px-2 py-0.5 rounded-full"
                          :class="{
                            'bg-red-200 text-red-700': insight.type === 'critical',
                            'bg-amber-200 text-amber-700': insight.type === 'warning',
                            'bg-emerald-200 text-emerald-700': insight.type === 'positive',
                            'bg-blue-200 text-blue-700': insight.type === 'info'
                          }">
                          {{ insight.type === 'critical' ? 'Crítico' : 
                             insight.type === 'warning' ? 'Atenção' : 
                             insight.type === 'positive' ? 'Positivo' : 'Informação' }}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Decoração visual -->
                  <div class="absolute bottom-0 right-0 w-24 h-24 opacity-10"
                    :class="{
                      'bg-gradient-to-br from-red-400 to-red-600': insight.type === 'critical',
                      'bg-gradient-to-br from-amber-400 to-amber-600': insight.type === 'warning',
                      'bg-gradient-to-br from-emerald-400 to-emerald-600': insight.type === 'positive',
                      'bg-gradient-to-br from-blue-400 to-blue-600': insight.type === 'info'
                    }"
                    style="border-radius: 50% 0 0 0; transform: translate(30%, 30%);">
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Call to action opcional
            <div v-if="insights.some(i => i.type === 'critical' || i.type === 'warning')" 
              class="mt-5 pt-5 border-t border-gray-200">
              <p class="text-sm text-gray-600 text-center">
                <span class="i-lucide-info mr-1"></span>
                Precisa de ajuda para melhorar esses indicadores? 
                <a href="#" class="text-blue-600 hover:text-blue-700 font-medium hover:underline">
                  Entre em contato com nosso suporte
                </a>
              </p>
            </div>
            -->
          </div>
        </div>
        
        <!-- Volume e Engajamento -->
        <div class="mb-8">
          <h3 class="text-lg font-semibold mb-4 flex items-center text-n-slate-12">
            <span class="i-lucide-bar-chart-2 mr-2 text-n-blue-text"></span>
            Volume e Engajamento
          </h3>
          
          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <div class="outline-1 outline outline-n-container relative shadow hover:shadow-md rounded-xl p-5 transition-all duration-300 bg-n-solid-2">
              <div class="flex items-center justify-between mb-2">
                <div class="flex items-center">
                  <span class="i-lucide-message-square text-n-blue-text mr-2 text-lg"></span>
                  <h4 class="text-sm font-semibold m-0 text-n-slate-12">Total de Conversas</h4>
                </div>
                <div class="group relative">
                  <span class="i-lucide-info text-n-slate-11 text-xs cursor-help hover:text-n-slate-12 transition-colors"></span>
                    <div class="invisible group-hover:visible absolute right-0 top-6 w-56 p-2 bg-n-solid-2 rounded-lg shadow-xl z-10 text-xs text-n-slate-11 border border-n-weak">
                    Número total de conversas finalizadas no período selecionado.<br><br>
                    <strong>Conversa</strong> representa cada conjunto finalizado de interações entre cliente e equipe, podendo haver várias conversas com o mesmo contato.
                    </div>
                </div>
              </div>
              <div class="mt-2 mb-0 text-2xl font-bold text-n-slate-12">
                {{ reportData.totalConversations.toLocaleString() }}
              </div>
              <p class="text-xs text-n-blue-text mt-1 font-medium">conversas no período</p>
            </div>
            
            <div class="outline-1 outline outline-n-container relative shadow hover:shadow-md rounded-xl p-5 transition-all duration-300 bg-n-solid-2">
              <div class="flex items-center justify-between mb-2">
                <div class="flex items-center">
                  <span class="i-lucide-mail text-n-blue-text mr-2 text-lg"></span>
                  <h4 class="text-sm font-semibold m-0 text-n-slate-12">Volume de Mensagens</h4>
                </div>
                <div class="group relative">
                  <span class="i-lucide-info text-n-slate-11 text-xs cursor-help hover:text-n-slate-12 transition-colors"></span>
                  <div class="invisible group-hover:visible absolute right-0 top-6 w-56 p-2 bg-n-solid-2 rounded-lg shadow-xl z-10 text-xs text-n-slate-11 border border-n-weak">
                    Total de mensagens trocadas entre clientes e agentes.
                  </div>
                </div>
              </div>
              <div class="mt-2 mb-0 text-2xl font-bold text-n-slate-12">
                {{ reportData.totalMessagesSent.toLocaleString() }}
              </div>
              <p class="text-xs text-n-blue-text mt-1 font-medium">mensagens enviadas</p>
            </div>
            
            <div class="outline-1 outline outline-n-container relative shadow hover:shadow-md rounded-xl p-5 transition-all duration-300 bg-n-solid-2">
              <div class="flex items-center justify-between mb-2">
                <div class="flex items-center">
                  <span class="i-lucide-message-circle text-n-blue-text mr-2 text-lg"></span>
                  <h4 class="text-sm font-semibold m-0 text-n-slate-12">Interações de Clientes</h4>
                </div>
                <div class="group relative">
                  <span class="i-lucide-info text-n-slate-11 text-xs cursor-help hover:text-n-slate-12 transition-colors"></span>
                  <div class="invisible group-hover:visible absolute right-0 top-6 w-56 p-2 bg-n-solid-2 rounded-lg shadow-xl z-10 text-xs text-n-slate-11 border border-n-weak">
                    Quantidade de mensagens enviadas pelos clientes.
                  </div>
                </div>
              </div>
              <div class="mt-2 mb-0 text-2xl font-bold text-n-slate-12">
                {{ reportData.customerMessageCount.toLocaleString() }}
              </div>
              <p class="text-xs text-n-blue-text mt-1 font-medium">mensagens de clientes</p>
            </div>
            
            <div class="outline-1 outline outline-n-container relative shadow hover:shadow-md rounded-xl p-5 transition-all duration-300 bg-n-solid-2">
              <div class="flex items-center justify-between mb-2">
                <div class="flex items-center">
                  <span class="i-lucide-user-plus text-n-blue-text mr-2 text-lg"></span>
                  <h4 class="text-sm font-semibold m-0 text-n-slate-12">Novos Clientes</h4>
                </div>
                <div class="group relative">
                  <span class="i-lucide-info text-n-slate-11 text-xs cursor-help hover:text-n-slate-12 transition-colors"></span>
                  <div class="invisible group-hover:visible absolute right-0 top-6 w-56 p-2 bg-n-solid-2 rounded-lg shadow-xl z-10 text-xs text-n-slate-11 border border-n-weak">
                    Clientes que conversaram pela primeira vez no período.
                  </div>
                </div>
              </div>
              <div class="mt-2 mb-0 text-2xl font-bold text-n-slate-12">
                {{ reportData.newCustomerRate.toLocaleString() }}
              </div>
              <p class="text-xs text-n-blue-text mt-1 font-medium">novos contatos</p>
            </div>
          </div>
        </div>
        
        <!-- Análise de Conversão Unificada -->
        <div class="mb-10" v-if="hasFunnelData">
          <h2 class="text-xl font-semibold mb-6 flex items-center text-n-slate-12">
            <span class="i-lucide-trending-up mr-2 text-green-600"></span>
            Análise de Conversão
          </h2>
          
          <div class="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p class="text-sm text-green-800 flex items-center">
              <span class="i-lucide-info mr-1"></span>
              Visão completa do funil de vendas e performance em cada tipo de conversão.
            </p>
          </div>

          <div class="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <!-- Coluna esquerda - Cards de métricas (4 colunas) -->
            <div class="lg:col-span-4 space-y-4">
              <h3 class="outline-1 outline outline-n-container relative shadow hover:shadow-md rounded-xl p-5 transition-all duration-300 bg-n-solid-2 flex items-center text-base font-bold m-0">
                <span class="i-lucide-filter mr-2 text-purple-600 text-xl flex items-center"></span>
                Taxas de Conversão 
              </h3>
            <!-- Qualificação SDR -->
            <div v-if="reportData.sdrQualificationRate > 0" class="outline-1 outline outline-n-container relative shadow hover:shadow-md rounded-xl p-5 transition-all duration-300 bg-n-solid-2">
              <div class="relative">
                <div class="flex items-center justify-between mb-3">
                  <div class="flex items-center">
                    <div class="p-2 bg-woot-100 rounded-lg mr-3 flex">
                      <span class="i-lucide-clipboard-check text-n-blue-text text-xl"></span>
                    </div>
                    <h4 class="text-base font-bold m-0">Qualificação SDR</h4>
                  </div>
                  <div class="group relative">
                    <span class="i-lucide-info text-n-slate-11 text-sm cursor-help hover:text-n-slate-12 transition-colors"></span>
                    <div class="invisible group-hover:visible absolute right-0 top-6 w-56 p-2 bg-n-solid-2 rounded-lg shadow-xl z-10 text-xs text-n-slate-11 border border-n-weak">
                      <strong class="">Qualificação SDR:</strong> Percentual de leads qualificados como potenciais clientes.
                    </div>
                  </div>
                </div>
                <div class="mt-3 mb-1 text-3xl font-semibold text-n-slate-12">
                    {{ reportData.sdrQualificationRate.toFixed(1) }}%
                </div>
                <div class="text-sm text-n-slate-11 mb-2">
                    {{ reportData.sdrQualificationCount }} qualificações
                </div>
                <div class="h-1 w-full rounded-full mt-3 overflow-hidden bg-woot-100">
                  <div class="h-full bg-n-blue-text rounded-full transition-all duration-1000" :style="{width: reportData.sdrQualificationRate + '%'}"></div>
                </div>
              </div>
            </div>
            
            <!-- Vendas Realizadas -->
            <div v-if="reportData.salesCompletionRate > 0" class="outline-1 outline outline-n-container relative shadow hover:shadow-md rounded-xl p-5 transition-all duration-300 bg-n-solid-2">
              <div class="relative">
                <div class="flex items-center justify-between mb-3">
                  <div class="flex items-center">
                    <div class="p-2 bg-green-100 rounded-lg mr-3 flex">
                      <span class="i-lucide-shopping-cart text-green-600 text-xl"></span>
                    </div>
                    <h4 class="text-base font-bold m-0">Vendas Fechadas</h4>
                  </div>
                  <div class="group relative">
                    <span class="i-lucide-info text-n-slate-11 text-sm cursor-help hover:text-n-slate-12 transition-colors"></span>
                    <div class="invisible group-hover:visible absolute right-0 top-6 w-56 p-2 bg-n-solid-2 rounded-lg shadow-xl z-10 text-xs text-n-slate-11 border border-n-weak">
                      <strong class="">Taxa de Fechamento:</strong> Percentual de leads que se converteram em vendas.
                    </div>
                  </div>
                </div>
                <div class="mt-3 mb-1 text-3xl font-semibold text-n-slate-12">
                    {{ reportData.salesCompletionRate.toFixed(1) }}%
                </div>
                <div class="text-sm text-n-slate-11 mb-2">
                    {{ reportData.salesCompletionCount }} vendas
                </div>
                <div class="h-1 w-full rounded-full mt-3 overflow-hidden bg-green-100">
                  <div class="h-full bg-green-600 rounded-full transition-all duration-1000" :style="{width: reportData.salesCompletionRate + '%'}"></div>
                </div>
              </div>
            </div>
            
            <!-- Agendamentos -->
            <div v-if="reportData.appointmentCompletionRate > 0" class="outline-1 outline outline-n-container relative shadow hover:shadow-md rounded-xl p-5 transition-all duration-300 bg-n-solid-2">
              <div class="relative">
                <div class="flex items-center justify-between mb-3">
                  <div class="flex items-center">
                    <div class="p-2 bg-indigo-100 rounded-lg mr-3 flex">
                      <span class="i-lucide-calendar-check text-indigo-600 text-xl"></span>
                    </div>
                    <h4 class="text-base font-bold m-0">Agendamentos</h4>
                  </div>
                  <div class="group relative">
                    <span class="i-lucide-info text-n-slate-11 text-sm cursor-help hover:text-n-slate-12 transition-colors"></span>
                    <div class="invisible group-hover:visible absolute right-0 top-6 w-56 p-2 bg-n-solid-2 rounded-lg shadow-xl z-10 text-xs text-n-slate-11 border border-n-weak">
                      <strong class="">Taxa de Agendamento:</strong> Percentual de leads que converteram um agendamento.
                    </div>
                  </div>
                </div>
                <div class="mt-3 mb-1 text-3xl font-semibold text-n-slate-12">
                    {{ reportData.appointmentCompletionRate.toFixed(1) }}%
                </div>
                <div class="text-sm text-n-slate-11 mb-2">
                    {{ reportData.appointmentCompletionCount }} agendamentos
                </div>
                <div class="h-1 w-full rounded-full mt-3 overflow-hidden bg-indigo-100">
                  <div class="h-full bg-indigo-600 rounded-full transition-all duration-1000" :style="{width: reportData.appointmentCompletionRate + '%'}"></div>
                </div>
              </div>
            </div>
            </div>
            
            <!-- Coluna direita - Gráficos (8 colunas) -->
            <div class="lg:col-span-8 space-y-6">
              <!-- Evolução das Conversões -->
              <div class="shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 p-5">
                <h3 class="text-lg font-medium mb-4 flex items-center">
                  <span class="i-lucide-activity mr-2 text-green-600"></span>
                  Evolução das Conversões
                </h3>
                <div class="h-64">
                  <LineChart 
                    v-if="!isConversionChartEmpty"
                    :collection="conversionChartData" 
                    :chart-options="lineChartOptions"
                  />
                  <div v-else class="flex items-center justify-center h-full">
                    <div class="text-center flex flex-col items-center">
                      <span class="i-lucide-activity text-4xl text-gray-400 mb-3 block"></span>
                      <p class="text-gray-500 text-sm max-w-xs">Não há dados de evolução de conversões disponíveis para o período selecionado</p>
                    </div>
                  </div>
                </div>
                <p class="text-xs text-gray-600 mt-2">
                  Acompanhamento temporal do número de conversões e taxa de conversão.
                </p>
              </div>
              
              <!-- Transferências para Atendimento Humano -->
              <div class="shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 p-5">
                <h3 class="text-lg font-medium mb-4 flex items-center">
                  <span class="i-lucide-users mr-2 text-amber-600"></span>
                  Transferências para Atendimento Humano
                </h3>
                <div class="h-64 flex items-center justify-center">
                  <div class="text-center flex flex-col items-center">
                    <span class="i-lucide-clock text-4xl text-gray-400 mb-3 block"></span>
                    <p class="text-gray-500 text-sm max-w-xs">
                      Análise de transferências para atendimento humano estará disponível em breve, incluindo motivos e padrões de escalação.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
        
        <!-- Indicadores de Alerta -->
        <div class="mb-10">
          <h3 class="text-lg font-semibold mb-4 flex items-center text-n-slate-12">
            <span class="i-lucide-alert-triangle mr-2 text-red-600"></span>
            Indicadores de Alerta
          </h3>
          
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div class="outline-1 outline outline-n-container relative shadow hover:shadow-md rounded-xl p-5 transition-all duration-300 bg-n-solid-2">
              <div class="flex items-center justify-between mb-2">
                <div class="flex items-center">
                  <span class="i-lucide-user-x text-red-500 mr-2"></span>
                  <h4 class="text-sm font-medium m-0">Clientes Perdidos</h4>
                </div>
                <div class="group relative">
                  <span class="i-lucide-info text-n-slate-11 text-xs cursor-help"></span>
                  <div class="invisible group-hover:visible absolute right-0 top-6 w-56 p-2 bg-n-solid-2 rounded-lg shadow-xl z-10 text-xs text-n-slate-11 border border-n-weak">
                    Clientes que abandonaram a conversa ou desistiram do processo.
                  </div>
                </div>
              </div>
              <div class="mt-1 mb-0 text-2xl font-semibold" 
                  :class="reportData.customersLostCount > 0 ? 'text-n-slate-12' : 'text-n-slate-12'">
                {{ reportData.customersLostCount.toLocaleString() }}
              </div>
              <p class="text-xs mt-1" :class="reportData.customersLostCount > 0 ? ' font-medium' : 'text-n-slate-11'">
                {{ reportData.customersLostCount === 0 && reportData.totalConversations === 0 ? '' : reportData.customersLostCount > 0 ? '' : 'Sem perdas' }}
              </p>
            </div>
            
            <!-- Cliente Desqualificado -->
            <div class="outline-1 outline outline-n-container relative shadow hover:shadow-md rounded-xl p-5 transition-all duration-300 bg-n-solid-2">
              <div class="flex items-center justify-between mb-2">
                <div class="flex items-center">
                  <span class="i-lucide-user-minus text-orange-500 mr-2"></span>
                  <h4 class="text-sm font-medium m-0">Clientes Desqualificados</h4>
                </div>
                <div class="group relative">
                  <span class="i-lucide-info text-n-slate-11 text-xs cursor-help"></span>
                  <div class="invisible group-hover:visible absolute right-0 top-6 w-56 p-2 bg-n-solid-2 rounded-lg shadow-xl z-10 text-xs text-n-slate-11 border border-n-weak">
                    Clientes que não se encaixaram no perfil ideal ou critérios de qualificação.
                  </div>
                </div>
              </div>
              <div class="mt-1 mb-0 text-2xl font-semibold" 
                  :class="reportData.disqualifiedCount > 0 ? 'text-n-slate-12' : 'text-n-slate-12'">
                {{ reportData.disqualifiedCount.toLocaleString() }}
              </div>
              <p class="text-xs mt-1" :class="reportData.disqualificationRate > 10 ? 'text-orange-600 font-medium' : 'text-n-slate-11'">
                {{ reportData.disqualifiedCount === 0 && reportData.totalConversations === 0 ? '' : reportData.disqualificationRate > 10 ? `${reportData.disqualificationRate.toFixed(1)}% de desqualificação` : reportData.disqualifiedCount > 0 ? `${reportData.disqualificationRate.toFixed(1)}% de desqualificação` : 'Sem desqualificações' }}
              </p>
            </div>
            
            <div class="outline-1 outline outline-n-container relative shadow hover:shadow-md rounded-xl p-5 transition-all duration-300 bg-n-solid-2">
              <div class="flex items-center justify-between mb-2">
                <div class="flex items-center">
                  <span class="i-lucide-message-square-warning text-amber-500 mr-2"></span>
                  <h4 class="text-sm font-medium m-0">Linguagem Inadequada</h4>
                </div>
                <div class="group relative">
                  <span class="i-lucide-info text-n-slate-11 text-xs cursor-help"></span>
                  <div class="invisible group-hover:visible absolute right-0 top-6 w-56 p-2 bg-n-solid-2 rounded-lg shadow-xl z-10 text-xs text-n-slate-11 border border-n-weak">
                    Percentual de conversas com linguagem ofensiva ou inadequada.<br><br>
                    <strong>Conversa</strong> representa cada conjunto finalizado de interações entre cliente e equipe, podendo haver várias conversas com o mesmo contato.
                  </div>
                </div>
              </div>
              <div class="mt-1 mb-0 text-2xl font-semibold" 
                  :class="reportData.inappropriateLanguage.customer > 5 ? 'text-n-slate-12' : 'text-n-slate-12'">
                {{ reportData.inappropriateLanguage.customer.toFixed(1) }}%
              </div>
              <p class="text-xs mt-1" :class="reportData.inappropriateLanguage.customer > 5 ? 'text-amber-600 font-medium' : 'text-n-slate-11'">
                {{ reportData.inappropriateLanguage.customer === 0 && reportData.totalConversations === 0 ? '' : reportData.inappropriateLanguage.customer > 5 ? 'Acima do aceitável' : 'Dentro do normal' }}
              </p>
            </div>
          </div>
        </div>
        
        <!-- Indicadores de Disparo -->
        <div class="mb-10">
          <h3 class="text-lg font-semibold mb-4 flex items-center text-n-slate-12">
            <span class="i-lucide-send mr-2 text-violet-600"></span>
            Métricas de Disparo
          </h3>
          
          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4">
            <div class="shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 p-5 hover:shadow-md transition-all duration-300">
              <div class="flex items-center justify-between mb-2">
                <div class="flex items-center">
                  <span class="i-lucide-send text-n-blue-text mr-2 text-xl"></span>
                  <h4 class="text-base font-semibold m-0 text-n-slate-12">Taxa de Disparo</h4>
                </div>
                <div class="group relative">
                  <span class="i-lucide-info text-n-slate-11 text-sm cursor-help hover:text-n-slate-12 transition-colors"></span>
                  <div class="invisible group-hover:visible absolute right-0 top-6 w-56 p-2 bg-n-solid-2 rounded-lg shadow-xl z-10 text-xs text-n-slate-11 border border-n-weak">
                    Percentual de conversas iniciadas por disparo ativo (outbound).<br><br>
                    <strong>Conversa</strong> representa cada conjunto finalizado de interações entre cliente e equipe, podendo haver várias conversas com o mesmo contato.
                  </div>
                </div>
              </div>
              <div class="mt-3 mb-1 text-3xl font-bold text-n-slate-12">
                {{ reportData.outboundRate.toFixed(1) }}%
              </div>
              <p class="text-sm text-n-slate-11 mt-2">
                <span v-if="reportData.outboundCount > 0">Total: {{ reportData.outboundCount.toLocaleString() }} disparos</span>
                <span v-else-if="reportData.totalConversations === 0">{{ '' }}</span>
                <span v-else>Nenhum disparo realizado</span>
              </p>
            </div>
            
            <div class="shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 p-5 hover:shadow-md transition-all duration-300">
              <div class="flex items-center justify-between mb-2">
                <div class="flex items-center">
                  <span class="i-lucide-target text-n-blue-text mr-2 text-xl"></span>
                  <h4 class="text-base font-semibold m-0 text-n-slate-12">Conversão de Disparos</h4>
                </div>
                <div class="group relative">
                  <span class="i-lucide-info text-n-slate-11 text-sm cursor-help hover:text-n-slate-12 transition-colors"></span>
                  <div class="invisible group-hover:visible absolute right-0 top-6 w-56 p-2 bg-n-solid-2 rounded-lg shadow-xl z-10 text-xs text-n-slate-11 border border-n-weak">
                    Percentual de conversas iniciadas por disparo que resultaram em conversão.<br><br>
                    <strong>Conversa</strong> representa cada conjunto finalizado de interações entre cliente e equipe, podendo haver várias conversas com o mesmo contato.
                  </div>
                </div>
              </div>
              <div class="mt-3 mb-1 text-3xl font-bold text-n-slate-12">
                {{ reportData.outboundConversionRate.toFixed(1) }}%
              </div>
              <p class="text-sm mt-2" 
                  :class="reportData.outboundConversionRate > 20 ? 'text-emerald-600 font-semibold' : 'text-amber-600'">
                {{ reportData.outboundConversionRate === 0 && reportData.totalConversations === 0 ? '' : reportData.outboundConversionRate > 20 ? 'Excelente performance' : 'Oportunidade de melhoria' }}
              </p>
            </div>
          </div>
        </div>
        
        <!-- Análise Gráfica Detalhada -->
        <div class="mb-10">
          <h2 class="text-xl font-semibold mb-6 flex items-center text-n-slate-12">
            <span class="i-lucide-bar-chart-3 mr-2 text-indigo-600"></span>
            Análise Gráfica Detalhada
          </h2>

          <!-- Tabs de navegação entre gráficos -->
          <div class="bg-n-solid-1 rounded-lg p-1 flex mb-6 border border-n-weak inline-flex gap-1">
            <button 
              class="px-3 py-1.5 text-sm rounded-md flex items-center"
              :class="activeGraphTab === 'satisfaction' ? 'bg-n-brand text-white' : 'text-n-slate-11 hover:text-n-slate-12'"
              @click="activeGraphTab = 'satisfaction'"
            >
              <span class="i-lucide-smile mr-1.5"></span>
              Análise de Satisfação
            </button>
            <button 
              class="px-3 py-1.5 text-sm rounded-md flex items-center"
              :class="activeGraphTab === 'sentiment' ? 'bg-n-brand text-white' : 'text-n-slate-11 hover:text-n-slate-12'"
              @click="activeGraphTab = 'sentiment'"
            >
              <span class="i-lucide-heart mr-1.5"></span>
              Análise de Sentimento
            </button>
          </div>
          
          <!-- Gráficos de Satisfação -->
          <div v-if="activeGraphTab === 'satisfaction'">
            <div class="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p class="text-sm text-blue-800 flex items-center">
                <span class="i-lucide-info mr-1"></span>
                Visualizações focadas na satisfação e qualidade do atendimento ao cliente.
              </p>
            </div>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div class="shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 p-4">
                <h3 class="text-lg font-medium mb-4 flex items-center">
                  <span class="i-lucide-pie-chart mr-2 text-blue-600"></span>
                  Distribuição de Reações dos Clientes
                </h3>
                <div class="h-80 overflow-visible">
                  <ClickableDonutChart 
                    v-if="!isReactionDonutEmpty"
                    :collection="reactionDonutData" 
                    :chart-options="donutChartOptions"
                    @slice-click="(payload) => handleDonutClick(payload, 'customer_reaction')"
                  />
                  <div v-else class="flex items-center justify-center h-full">
                    <div class="text-center flex flex-col items-center">
                      <span class="i-lucide-pie-chart text-4xl text-gray-400 mb-3 block"></span>
                      <p class="text-gray-500 text-sm max-w-xs">Não há dados de reações disponíveis para o período selecionado</p>
                    </div>
                  </div>
                </div>
                <p class="text-xs text-gray-600 mt-2">
                  Proporção de reações positivas, neutras e negativas identificadas nas conversas.
                </p>
              </div>
              
              <div class="shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 p-4">
                <h3 class="text-lg font-medium mb-4 flex items-center">
                  <span class="i-lucide-check-circle mr-2 text-teal-600"></span>
                  Taxa de Resolução de Problemas
                </h3>
                <div class="h-80 overflow-visible">
                  <ClickableDonutChart 
                    v-if="!isProblemResolutionDonutEmpty"
                    :collection="problemResolutionDonutData" 
                    :chart-options="donutChartOptions"
                    @slice-click="(payload) => handleDonutClick(payload, 'problem_resolution')"
                  />
                  <div v-else class="flex items-center justify-center h-full">
                    <div class="text-center flex flex-col items-center">
                      <span class="i-lucide-check-circle text-4xl text-gray-400 mb-3 block"></span>
                      <p class="text-gray-500 text-sm max-w-xs">Não há dados de resolução disponíveis para o período selecionado</p>
                    </div>
                  </div>
                </div>
                <p class="text-xs text-gray-600 mt-2">
                  Percentual de problemas e dúvidas efetivamente resolvidos versus não resolvidos.
                </p>
              </div>
            </div>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div class="shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 p-4">
                <h3 class="text-lg font-medium mb-4 flex items-center">
                  <span class="i-lucide-bar-chart-2 mr-2 text-purple-600"></span>
                  Distribuição de Avaliações
                </h3>
                <div class="h-64">
                  <BarChart 
                    v-if="!isRatingChartEmpty"
                    :collection="ratingChartData" 
                    :chart-options="barChartOptions"
                    @bar-click="handleRatingBarClick" 
                  />
                  <div v-else class="flex items-center justify-center h-full">
                    <div class="text-center flex flex-col items-center">
                      <span class="i-lucide-bar-chart-2 text-4xl text-gray-400 mb-3 block"></span>
                      <p class="text-gray-500 text-sm max-w-xs">Não há dados de avaliações disponíveis para o período selecionado</p>
                    </div>
                  </div>
                </div>
                <p class="text-xs text-gray-600 mt-2">
                  Quantidade de conversas por faixa de avaliação de satisfação (0-100%).
                </p>
              </div>
              
              <div class="shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 p-4">
                <h3 class="text-lg font-medium mb-4 flex items-center">
                  <span class="i-lucide-trending-up mr-2 text-blue-600"></span>
                  Evolução da Satisfação
                </h3>
                <div class="h-64">
                  <LineChart 
                    v-if="!isTimeChartEmpty"
                    :collection="timeChartData" 
                    :chart-options="satisfactionLineChartOptions"
                  />
                  <div v-else class="flex items-center justify-center h-full">
                    <div class="text-center flex flex-col items-center">
                      <span class="i-lucide-trending-up text-4xl text-gray-400 mb-3 block"></span>
                      <p class="text-gray-500 text-sm max-w-xs">Não há dados de evolução de satisfação disponíveis para o período selecionado</p>
                    </div>
                  </div>
                </div>
                <p class="text-xs text-gray-600 mt-2">
                  Evolução temporal do índice médio de satisfação dos clientes.
                </p>
              </div>
            </div>
          </div>
          
          
          <!-- Gráficos de Sentimento -->
          <div v-if="activeGraphTab === 'sentiment'">
            <div class="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p class="text-sm text-blue-800 flex items-center">
                <span class="i-lucide-info mr-1"></span>
                Análise de sentimentos e empatia identificados nas interações entre agentes e clientes.
              </p>
            </div>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div class="shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 p-4">
                <h3 class="text-lg font-medium mb-4 flex items-center">
                  <span class="i-lucide-heart mr-2 text-pink-600"></span>
                  Níveis de Empatia do Agente
                </h3>
                <div class="h-80 overflow-visible">
                  <ClickableDonutChart 
                    v-if="!isAgentEmpathyDonutEmpty"
                    :collection="agentEmpathyDonutData" 
                    :chart-options="donutChartOptions"
                    @slice-click="(payload) => handleDonutClick(payload, 'agent_empathy')"
                  />
                  <div v-else class="flex items-center justify-center h-full">
                    <div class="text-center flex flex-col items-center">
                      <span class="i-lucide-heart text-4xl text-gray-400 mb-3 block"></span>
                      <p class="text-gray-500 text-sm max-w-xs">Não há dados de empatia disponíveis para o período selecionado</p>
                    </div>
                  </div>
                </div>
                <p class="text-xs text-gray-600 mt-2">
                  Distribuição dos níveis de empatia demonstrados pelos agentes durante o atendimento.
                </p>
              </div>
              
              <div class="shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 p-4">
                <h3 class="text-lg font-medium mb-4 flex items-center">
                  <span class="i-lucide-smile mr-2 text-blue-600"></span>
                  Sentimento dos Agentes
                </h3>
                <div class="h-80 overflow-visible">
                  <ClickableDonutChart 
                    v-if="!isAgentSentimentDonutEmpty"
                    :collection="agentSentimentDonutData" 
                    :chart-options="donutChartOptions"
                    @slice-click="(payload) => handleDonutClick(payload, 'agent_sentiment')"
                  />
                  <div v-else class="flex items-center justify-center h-full">
                    <div class="text-center flex flex-col items-center">
                      <span class="i-lucide-smile text-4xl text-gray-400 mb-3 block"></span>
                      <p class="text-gray-500 text-sm max-w-xs">Não há dados de sentimento disponíveis para o período selecionado</p>
                    </div>
                  </div>
                </div>
                <p class="text-xs text-gray-600 mt-2">
                  Análise do tom e sentimento geral demonstrado pelos agentes nas conversas.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        
        <!-- Navegação para outras páginas -->
        <div class="mt-8 text-center">
          <p class="text-sm text-n-slate-11 mb-3">Explore análises mais detalhadas:</p>
          <div class="flex justify-center space-x-4">
            <Button
              label="Análise de Conteúdo"
              icon="i-lucide-list"
              color="blue"
              @click="navegarParaTabelas"
            />
          </div>
        </div>
      </div>
    </div>
  
  <!-- Modal de Conversas -->
  <ConversationPopUp
    v-if="showConversationModal"
    :conversations="modalConversations"
    :title="modalTitle"
    :loading="isLoadingModalData"
    :subtitle="'Mostrando as 10 conversas mais recentes'"
    @close="closeConversationModal"
  />
</template>

<style scoped>
.reports--wrapper :deep(.multiselect__tags),
.reports--wrapper :deep(.multiselect__single),
.reports--wrapper :deep(.multiselect__input) {
  @apply bg-n-alpha-3 border-n-weak text-n-slate-12 rounded-lg text-sm min-h-[2.5rem];
}

.reports--wrapper :deep(.multiselect__placeholder) {
  @apply text-n-slate-11;
}

.reports--wrapper :deep(.mx-input-wrapper) {
  @apply bg-n-alpha-3 border-n-weak text-n-slate-12 rounded-lg text-sm;
}

.reports--wrapper :deep(.mx-input-wrapper input) {
  @apply text-n-slate-11;
}

/* Animações suaves */
.shadow {
  transition: all 0.3s ease;
}

.group:hover .invisible {
  visibility: visible;
  opacity: 1;
  transition: opacity 0.2s ease-in-out;
}

/* Animação de pulse para elementos críticos */
@keyframes pulse {
  0%, 100% {
    opacity: 0.3;
  }
  50% {
    opacity: 0.5;
  }
}

.animate-ping {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

/* Efeito de hover suave para os cards de insight */
.group:hover {
  transform: scale(1.02);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Animação de entrada para os insights */
@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.reports--wrapper [v-for] > div {
  animation: slideInUp 0.5s ease-out;
  animation-fill-mode: both;
}

.reports--wrapper [v-for] > div:nth-child(1) {
  animation-delay: 0.1s;
}

.reports--wrapper [v-for] > div:nth-child(2) {
  animation-delay: 0.2s;
}

.reports--wrapper [v-for] > div:nth-child(3) {
  animation-delay: 0.3s;
}

.reports--wrapper [v-for] > div:nth-child(4) {
  animation-delay: 0.4s;
}

.reports--wrapper [v-for] > div:nth-child(5) {
  animation-delay: 0.5s;
}

</style>